import { useEffect } from 'react';

import { useMessageInstanceEvent } from '../index';
import { useMessageContext } from '../message-context';
import { postNarrowExposureDrop } from './drop-event';
import { evaluateCohort } from './evaluate-cohort';
import type { NarrowExposureConfig } from './types';

/**
 * Narrow exposure check for a message the choreographer won't offer a slot, while the
 * choreographer is in use. Choreographed messages use `useNarrowExposureShouldClaim` instead.
 *
 * Returns `true` when the message has no experiment. Otherwise each render reads the cohort, which
 * logs the exposure, and only show cohorts render. Drop and unevaluated cohorts post a `dropped`
 * event after commit, with cohort `unevaluated` when Statsig returned no usable cohort.
 */
export const useNarrowExposureShouldRender = (
	config: NarrowExposureConfig | null | undefined,
): boolean => {
	const { messageInstanceId } = useMessageContext();
	const { messageInstanceDropped } = useMessageInstanceEvent();
	const decision = config ? evaluateCohort(config) : undefined;

	// The cohort can change between renders, so check each commit; the drop helper deduplicates.
	useEffect(() => {
		if (config && decision && decision.outcome !== 'show') {
			postNarrowExposureDrop(messageInstanceDropped, messageInstanceId, config, decision);
		}
	});

	return !decision || decision.outcome === 'show';
};
